import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, DatePicker } from "antd";
import dayjs from "dayjs";
import { MoonStars } from "@phosphor-icons/react";
import {
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
  InboxModalErrorBanner,
} from "../AIPane/AIPaneStyled";

/**
 * Custom snooze modal (FE Phase 5b).
 *
 * Lets the user pick an arbitrary future date/time. Reached via the
 * "Custom…" entry in the snooze dropdown's preset menu when the
 * 4h/24h/3d/1w presets aren't right.
 *
 * Validation
 * ----------
 * - Disable past dates and the "now" minute via `disabledDate` +
 *   `disabledTime` (the BE rejects past timestamps with 400, but
 *   client-side validation makes the UI feel snappier).
 * - The OK button stays disabled until the picker has a value AND
 *   that value is strictly in the future at submit time. Edge case:
 *   if the user picks 5 minutes ahead, then sits on the modal for
 *   10 minutes, the moment-of-submit guard catches it.
 *
 * Submission
 * ----------
 * Confirms via `onConfirm(isoString)` — the parent
 * (`InboxActionFooter`) owns the actual dispatch + invalidation
 * pipeline so this modal stays UI-only.
 */

const SnoozeCustomModal = ({ open, onClose, onConfirm, busy = false }) => {
  const [pickedAt, setPickedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Default to "tomorrow at 9 AM" — the most common "back to me
    // when I get to my desk" snooze.
    setPickedAt(dayjs().add(1, "day").hour(9).minute(0).second(0));
    setError(null);
  }, [open]);

  const handleConfirm = useCallback(() => {
    if (!pickedAt) return;
    if (!pickedAt.isAfter(dayjs())) {
      setError("Pick a time in the future.");
      return;
    }
    setError(null);
    onConfirm(pickedAt.toISOString());
  }, [pickedAt, onConfirm]);

  const disabledDate = useCallback((current) => {
    if (!current) return false;
    return current.isBefore(dayjs().startOf("day"));
  }, []);

  const disabledTime = useCallback(
    (current) => {
      if (!current || !current.isSame(dayjs(), "day")) return {};
      const now = dayjs();
      return {
        disabledHours: () =>
          Array.from({ length: now.hour() }, (_, i) => i),
        disabledMinutes: (selectedHour) => {
          if (selectedHour === now.hour()) {
            return Array.from({ length: now.minute() + 1 }, (_, i) => i);
          }
          return [];
        },
      };
    },
    []
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Snooze"
      cancelText="Cancel"
      okButtonProps={{
        disabled: !pickedAt || !pickedAt.isAfter(dayjs()) || busy,
        loading: busy,
      }}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <MoonStars size={16} weight="bold" />
          Snooze until…
        </span>
      }
      destroyOnClose
    >
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-snooze-until">
          Bring this back to my inbox at
        </InboxModalLabel>
        <DatePicker
          id="inbox-snooze-until"
          showTime={{ format: "HH:mm" }}
          format="YYYY-MM-DD HH:mm"
          value={pickedAt}
          onChange={setPickedAt}
          disabledDate={disabledDate}
          disabledTime={disabledTime}
          style={{ width: "100%" }}
        />
        <InboxModalHint>
          The document stays in the snoozed-items view until then, and
          the unread badge ignores it (Gmail/Slack semantics).
        </InboxModalHint>
      </InboxModalFieldGroup>

      {error && <InboxModalErrorBanner role="alert">{error}</InboxModalErrorBanner>}
    </Modal>
  );
};

SnoozeCustomModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

export default SnoozeCustomModal;
